import { useEffect, useRef, useState } from 'react';
import { LOCALE_DIR, LOCALE_NAMES, LOCALE_SHORT, offeredLocales, otherLocale } from '../lib/locale';
import { useLocale, useStrings } from '../state/useLocale';
import { sound } from '../lib/sound';

/**
 * The switch between the languages.
 *
 * Mounted at the root beside the sound control, not filed under a settings
 * page, for the same reason that one is: somebody who has landed on the wrong
 * language cannot navigate a menu written in it. It has to be reachable from
 * the first screen, from the middle of a level, and from a chapter essay, and
 * the only place that is true of is the corner of the window.
 *
 * It says what it will switch *to*, in that language's own script — `العربية`
 * when the page is English, `EN` when it is Arabic. A control offering
 * "Arabic" to an Arabic speaker is written in the language they came here to
 * leave; an endonym is legible to the person who needs it.
 *
 * Changing the language does not reload. The page is running a Linux virtual
 * machine that cost a hundred megabytes and a boot to get, and throwing that
 * away over a language switch would make the switch something a learner
 * learns not to touch. The catalogue is re-overlaid in place instead
 * (engine/runtime.ts, `applyLocale`) and the tree re-renders.
 */
export function LangToggle() {
  const { locale } = useLocale();
  return offeredLocales(locale).length > 2 ? <LangMenu /> : <LangSwitch />;
}

function Globe() {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true" width="13" height="13">
      {/* A globe: a circle, its equator, and one meridian. Not a flag —
          no flag is a language, and picking one for Arabic would be
          picking a country out of twenty-two. */}
      <circle cx="8" cy="8" r="6" fill="none" stroke="currentColor" strokeWidth="1.3" />
      <path d="M2 8h12" fill="none" stroke="currentColor" strokeWidth="1.3" />
      <path d="M8 2c2.2 2 2.2 10 0 12C5.8 12 5.8 4 8 2z" fill="none" stroke="currentColor" strokeWidth="1.3" />
    </svg>
  );
}

/**
 * Three or more languages on offer: the same button, opening a list.
 *
 * A switch that cycled through three would make a reader who wanted the third
 * language pass through the second to get there — on a page that re-overlays
 * every level each time. So the button names the language the page is in, and
 * the list names every language in its own script.
 *
 * Today this is only drawn for somebody reading a preview language
 * (`offeredLocales`, lib/locale.ts); everybody else still gets the switch.
 */
function LangMenu() {
  const { locale, setLocale } = useLocale();
  const s = useStrings();
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const away = (e: PointerEvent) => {
      if (!root.current?.contains(e.target as Node)) setOpen(false);
    };
    const escape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', away);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('pointerdown', away);
      document.removeEventListener('keydown', escape);
    };
  }, [open]);

  return (
    <div ref={root}>
      <button
        className="lang"
        type="button"
        title={s.app.chooseLanguage}
        aria-label={s.app.chooseLanguage}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => {
          sound.click();
          setOpen((o) => !o);
        }}
      >
        <Globe />
        <span className="lang-code" lang={locale} dir={LOCALE_DIR[locale]}>
          {LOCALE_SHORT[locale]}
        </span>
      </button>
      {open && (
        <ul className="lang-menu" role="menu" aria-label={s.app.chooseLanguage}>
          {offeredLocales(locale).map((l) => (
            <li key={l} role="none">
              <button
                type="button"
                role="menuitemradio"
                aria-checked={l === locale}
                lang={l}
                onClick={() => {
                  sound.click();
                  setOpen(false);
                  setLocale(l);
                }}
              >
                {LOCALE_NAMES[l]}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function LangSwitch() {
  const { locale, toggle } = useLocale();
  const s = useStrings();
  const next = otherLocale(locale);

  return (
    <button
      className="lang"
      type="button"
      title={s.app.switchTo(LOCALE_NAMES[next])}
      aria-label={s.app.switchTo(LOCALE_NAMES[next])}
      onClick={() => {
        sound.click();
        toggle();
      }}
    >
      <Globe />
      {/* The code is in the *other* language, so it carries that language's
          direction: `EN` inside an Arabic document is a left-to-right island
          the layout has to be told about, and a two-letter one is exactly the
          size bidi gets wrong.

          On the code and not on the button, and that distinction is
          load-bearing. `inset-inline-end` resolves against the element's own
          direction, so a `dir` on a `position: fixed` button moves the button:
          with it there the switch sat in the *opposite* corner from the chat
          bubble and the sound control in both languages — bottom-left on an
          English page, and on an Arabic one bottom-right, on top of the
          workstation's help panel. The label needs the direction; the fixed
          box needs the page's. */}
      <span className="lang-code" lang={next} dir={LOCALE_DIR[next]}>
        {LOCALE_SHORT[next]}
      </span>
    </button>
  );
}
